import {averageOrderValueForPeriod} from "./reportingMetricPresentation.js";
import {calculatePnoPercent} from "./reportingMetricRatios.js";

function moneyAmount(money) {
    const amount = money?.amount;
    if (amount === null || amount === undefined || amount === "") {
        return null;
    }

    const numericAmount = Number(amount);
    return Number.isFinite(numericAmount) ? numericAmount : null;
}

function sumMoney(rows, getter) {
    let sum = 0;
    for (const row of rows) {
        const amount = moneyAmount(getter(row));
        if (amount === null) {
            return null;
        }
        sum += amount;
    }

    return sum;
}

function finiteNumber(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function dailyPeriodAvailability(period, snapshot) {
    if (snapshot?.complete === false) {
        return "incomplete";
    }

    return period?.orders ? "available" : "missing";
}

export function dailyCoreMetrics(period, snapshot) {
    const availability = dailyPeriodAvailability(period, snapshot);
    if (availability !== "available") {
        return {
            availability,
            ordersTotal: null,
            ordersBasis: null,
            turnover: null,
            marketingSpend: null,
            pno: null,
            averageOrderValue: null,
        };
    }

    const orders = period.orders;
    const centresAvailable = Array.isArray(orders.centres);
    const countsAvailable = finiteNumber(orders.ordersReceived) !== null
        && finiteNumber(orders.ordersCompleted) !== null;
    const ordersBasis = countsAvailable
        ? orders.ordersReceived + orders.ordersCompleted
        : null;
    const turnover = centresAvailable ? sumMoney(orders.centres, (row) => row.turnover) : null;
    const channelsAvailable = Array.isArray(period.channels?.topChannels);
    const marketingSpend = channelsAvailable
        ? sumMoney(period.channels.topChannels, (row) => row.spendMarketing)
        : null;

    return {
        availability,
        ordersTotal: finiteNumber(orders.ordersTotal),
        ordersBasis,
        turnover,
        marketingSpend,
        pno: turnover !== null && marketingSpend !== null
            ? calculatePnoPercent(marketingSpend, turnover)
            : null,
        averageOrderValue: turnover !== null && ordersBasis !== null
            ? averageOrderValueForPeriod(turnover, ordersBasis)
            : null,
    };
}

function hasNonZeroMoney(money) {
    const amount = moneyAmount(money);
    return amount !== null && amount !== 0;
}

function hasDailyActivity(data) {
    const orders = data?.orders;
    const orderCounts = [orders?.ordersTotal, orders?.ordersReceived, orders?.ordersCompleted, orders?.ordersCancelled];
    if (orderCounts.some((value) => Number.isFinite(value) && value !== 0)) return true;

    if ((orders?.centres ?? []).some((centre) => hasNonZeroMoney(centre?.turnover) || (Number.isFinite(centre?.orderAmount) && centre.orderAmount !== 0))) return true;
    if ((data?.channels?.topChannels ?? []).length > 0) return true;
    if (Object.values(data?.domains?.dataByDomain ?? {}).some((domain) => Object.values(domain?.turnoverGrossGranular ?? {}).some(hasNonZeroMoney))) return true;

    const independentRows = [
        data?.products?.topProductsByTurnoverGross,
        data?.coupons,
        data?.payments?.payments,
        data?.shippings?.shippings,
        data?.countries?.topCountries,
    ];
    return independentRows.some((rows) => Array.isArray(rows) && rows.length > 0);
}

export function dailyEmptyStateAlert(content) {
    return {
        id: "daily-empty",
        type: "warning",
        showClose: false,
        content,
    };
}

export function shouldShowDailyAvailabilityNotice({loading, availability, isEmpty, data, snapshot, selectedDay}) {
    if (availability === "available" || isEmpty || !loading || loading.core) {
        return false;
    }

    const waitingForIndependentPanels = Object.entries(loading)
        .some(([panel, isLoading]) => panel !== "core" && isLoading);
    const emptyUnpublishedDayCandidate = snapshot?.complete === false
        && snapshot.publishedDayCount === 0
        && Array.isArray(snapshot.missingDays)
        && snapshot.missingDays.includes(selectedDay)
        && !hasDailyActivity(data);

    return !(emptyUnpublishedDayCandidate && waitingForIndependentPanels);
}

export function isDailyReportGenuinelyEmpty({data, loading, selectedRequestKey, responseRequestKey, snapshot, selectedDay}) {
    if (!data || !loading || responseRequestKey !== selectedRequestKey || Object.values(loading).some(Boolean)) {
        return false;
    }

    const knownEmptySnapshot = snapshot?.complete === true;
    const unpublishedSelectedDay = snapshot?.complete === false
        && snapshot.publishedDayCount === 0
        && Array.isArray(snapshot.missingDays)
        && snapshot.missingDays.includes(selectedDay);
    if (!knownEmptySnapshot && !unpublishedSelectedDay) return false;

    if (hasDailyActivity(data)) return false;

    if (knownEmptySnapshot) {
        const counts = [data.orders?.ordersTotal, data.orders?.ordersReceived, data.orders?.ordersCompleted, data.orders?.ordersCancelled];
        if (!data.orders || counts.some((value) => !Number.isFinite(value)) || counts.some((value) => value !== 0)) {
            return false;
        }
    }

    return true;
}
